import { useEffect, useRef, useState, useReducer } from 'react'
import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Mention from '@tiptap/extension-mention'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'
import {
    MergeTagEnumData,
    MergeTagGroupEnumData,
    type MergeTagsByGroupEntry,
} from '@/Shared/AutoGenerated/Main'

export interface TipTapEditorProps {
    inputName?: string | null
    initialContent?: string | null
    heightInPixels?: number
    mergeTagGroups?: MergeTagsByGroupEntry[]
    onChange?: (html: string) => void
    className?: string
}

export function TipTapEditor({
    inputName = null,
    initialContent = null,
    heightInPixels = 300,
    mergeTagGroups = [],
    onChange,
    className = '',
}: TipTapEditorProps) {
    const editorElementRef = useRef<HTMLDivElement | null>(null)
    const [editor, setEditor] = useState<Editor | null>(null)
    const [editorContent, setEditorContent] = useState<string>(
        initialContent ?? '',
    )
    const [, forceUpdate] = useReducer((x) => x + 1, 0)

    useEffect(() => {
        if (!editorElementRef.current) {
            return
        }

        const editorInstance = new Editor({
            element: editorElementRef.current,
            content: initialContent ?? '',
            extensions: [
                StarterKit,
                Mention.configure({
                    HTMLAttributes: {
                        class: 'mention',
                    },
                    suggestion: {
                        char: '$',
                        items: () => [],
                    },
                }),
            ],
            onUpdate: ({ editor: updatedEditor }) => {
                const html = updatedEditor.getHTML()
                setEditorContent(html)
                onChange?.(html)
            },
            onTransaction: () => {
                forceUpdate()
            },
        })

        setEditor(editorInstance)

        return () => {
            editorInstance.destroy()
        }
    }, [])

    const handleMergeTagSelected = (mergeTag: string) => {
        if (!editor || !mergeTag) {
            return
        }
        const mergeTagHtml = `<span class="mention" data-type="mention" data-id="${mergeTag}" data-mention-suggestion-char="" contenteditable="false">$${mergeTag}</span>`
        editor.chain().focus().insertContent(mergeTagHtml).run()
    }

    const canUndo = editor?.can().undo() ?? false
    const canRedo = editor?.can().redo() ?? false
    const isBoldActive = editor?.isActive('bold') ?? false
    const isItalicActive = editor?.isActive('italic') ?? false
    const isUnderlineActive = editor?.isActive('underline') ?? false
    const isBulletListActive = editor?.isActive('bulletList') ?? false
    const isOrderedListActive = editor?.isActive('orderedList') ?? false

    return (
        <div className={className}>
            <div className="p-4 border rounded-xl bg-white space-y-4">
                <div
                    role="toolbar"
                    aria-label="Text formatting"
                    className="flex items-center flex-wrap gap-1"
                >
                    <button
                        type="button"
                        aria-label="Undo"
                        className={`px-2 py-1.5 rounded-lg2 border border-transparent hover:border-gray-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
                            !canUndo ? 'text-gray-300' : ''
                        }`}
                        onClick={() => editor?.chain().focus().undo().run()}
                        disabled={!canUndo}
                    >
                        <Icon
                            name={IconNameEnum.RotateLeft}
                            className="w-5 h-5"
                            aria-hidden="true"
                        />
                    </button>
                    <button
                        type="button"
                        aria-label="Redo"
                        className={`px-2 py-1.5 rounded-lg2 border border-transparent hover:border-gray-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
                            !canRedo ? 'text-gray-300' : ''
                        }`}
                        onClick={() => editor?.chain().focus().redo().run()}
                        disabled={!canRedo}
                    >
                        <Icon
                            name={IconNameEnum.RotateRight}
                            className="w-5 h-5"
                            aria-hidden="true"
                        />
                    </button>
                    <button
                        type="button"
                        aria-label="Bold"
                        aria-pressed={isBoldActive}
                        className={`px-2 py-1.5 rounded-lg2 border border-transparent hover:border-gray-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
                            isBoldActive ? 'bg-gray-200' : ''
                        }`}
                        onClick={() =>
                            editor?.chain().focus().toggleBold().run()
                        }
                    >
                        <Icon
                            name={IconNameEnum.Bold}
                            className="w-5 h-5"
                            aria-hidden="true"
                        />
                    </button>
                    <button
                        type="button"
                        aria-label="Italic"
                        aria-pressed={isItalicActive}
                        className={`px-2 py-1.5 rounded-lg2 border border-transparent hover:border-gray-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
                            isItalicActive ? 'bg-gray-200' : ''
                        }`}
                        onClick={() =>
                            editor?.chain().focus().toggleItalic().run()
                        }
                    >
                        <Icon
                            name={IconNameEnum.Italic}
                            className="w-5 h-5"
                            aria-hidden="true"
                        />
                    </button>
                    <button
                        type="button"
                        aria-label="Underline"
                        aria-pressed={isUnderlineActive}
                        className={`px-2 py-1.5 rounded-lg2 border border-transparent hover:border-gray-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
                            isUnderlineActive ? 'bg-gray-200' : ''
                        }`}
                        onClick={() => {
                            // If underline extension is configured in chain
                            const chain = editor?.chain().focus() as any
                            if (chain?.toggleUnderline) {
                                chain.toggleUnderline().run()
                            }
                        }}
                    >
                        <Icon
                            name={IconNameEnum.Underline}
                            className="w-5 h-5"
                            aria-hidden="true"
                        />
                    </button>
                    <button
                        type="button"
                        aria-label="Bullet list"
                        aria-pressed={isBulletListActive}
                        className={`px-2 py-1.5 rounded-lg2 border border-transparent hover:border-gray-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
                            isBulletListActive ? 'bg-gray-200' : ''
                        }`}
                        onClick={() =>
                            editor?.chain().focus().toggleBulletList().run()
                        }
                    >
                        <Icon
                            name={IconNameEnum.ListUl}
                            className="w-5 h-5"
                            aria-hidden="true"
                        />
                    </button>
                    <button
                        type="button"
                        aria-label="Ordered list"
                        aria-pressed={isOrderedListActive}
                        className={`px-2 py-1.5 rounded-lg2 border border-transparent hover:border-gray-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
                            isOrderedListActive ? 'bg-gray-200' : ''
                        }`}
                        onClick={() =>
                            editor?.chain().focus().toggleOrderedList().run()
                        }
                    >
                        <Icon
                            name={IconNameEnum.ListOl}
                            className="w-5 h-5"
                            aria-hidden="true"
                        />
                    </button>

                    {mergeTagGroups.length > 0 ? (
                        <>
                            <div className="px-2" />
                            <select
                                aria-label="Variables"
                                className="border border-gray-200 rounded-lg2 px-2 py-1"
                                defaultValue=""
                                onChange={(event) => {
                                    handleMergeTagSelected(event.target.value)
                                    event.target.value = ''
                                }}
                            >
                                <option
                                    value=""
                                    disabled
                                >
                                    Variables
                                </option>
                                {mergeTagGroups.map((mergeTagGroup) => (
                                    <optgroup
                                        key={mergeTagGroup.groupKey}
                                        label={
                                            MergeTagGroupEnumData[
                                                mergeTagGroup.groupKey
                                            ].label
                                        }
                                    >
                                        {mergeTagGroup.mergeTags.map(
                                            (mergeTag) => (
                                                <option
                                                    key={mergeTag}
                                                    value={
                                                        MergeTagEnumData[
                                                            mergeTag
                                                        ].wrappedTag
                                                    }
                                                >
                                                    {
                                                        MergeTagEnumData[
                                                            mergeTag
                                                        ].label
                                                    }
                                                </option>
                                            ),
                                        )}
                                    </optgroup>
                                ))}
                            </select>
                        </>
                    ) : null}
                </div>

                <div
                    className="p-4 bg-white border border-gray-200 rounded-xl ck-content overflow-y-scroll"
                    style={{
                        minHeight: `${heightInPixels}px`,
                        maxHeight: `${heightInPixels}px`,
                    }}
                >
                    <div ref={editorElementRef} />
                </div>
            </div>

            {inputName ? (
                <input
                    type="hidden"
                    name={inputName}
                    value={editorContent}
                />
            ) : null}
        </div>
    )
}
